import { useState } from "react"
import { Link } from "react-router-dom"
import { useMutation } from "@tanstack/react-query"
import { CheckCircle2, Send } from "lucide-react"
import { api, ApiError } from "@/lib/types"
import { useMe } from "@/lib/auth"

export function RequestAccess() {
  const me = useMe()

  const [message, setMessage] = useState("")
  const [termsRaw, setTermsRaw] = useState("")

  const submit = useMutation<unknown, ApiError, void>({
    mutationFn: async () => {
      const terms = termsRaw.split(",").map((s) => s.trim()).filter(Boolean)
      return api("/api/me/request-access", {
        method: "POST",
        json: { requestedTagTerms: terms, message: message.trim() || undefined },
      })
    },
  })

  if (me.data?.hasOpenRequest && !submit.isSuccess) {
    return (
      <div className="glass-section mx-auto max-w-xl text-center">
        <CheckCircle2 className="mx-auto text-th-accent" size={32} />
        <h1 className="mt-4 font-heading text-2xl">Your request is pending</h1>
        <p className="mt-2 text-sm text-th-fg/60">
          An admin will review it shortly. Once they grant tags, the matching concerts will appear in your catalog.
        </p>
        <Link to="/" className="btn mt-6">Back to catalog</Link>
      </div>
    )
  }

  if (submit.isSuccess) {
    return (
      <div className="glass-section mx-auto max-w-xl text-center">
        <CheckCircle2 className="mx-auto text-th-accent" size={32} />
        <h1 className="mt-4 font-heading text-2xl">Request submitted</h1>
        <p className="mt-2 text-sm text-th-fg/60">
          We'll let you know once an admin reviews it. Feel free to close this page.
        </p>
        <Link to="/" className="btn mt-6">Back to catalog</Link>
      </div>
    )
  }

  return (
    <div className="glass-section mx-auto max-w-xl">
      <h1 className="font-heading text-2xl font-semibold">Request access</h1>
      <p className="mt-2 text-sm text-th-fg/60">
        Tell us which seasons or events you should be able to see. An admin will review.
      </p>
      <form
        className="mt-6 space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (!submit.isPending) submit.mutate()
        }}
      >
        <label className="block">
          <div className="mb-1 text-xs text-th-fg/60">Tags or seasons you're requesting (comma-separated)</div>
          <input
            className="input w-full"
            placeholder="YGC162, Spring Tour 2024"
            value={termsRaw}
            onChange={(e) => setTermsRaw(e.target.value)}
          />
        </label>
        <label className="block">
          <div className="mb-1 text-xs text-th-fg/60">Message (optional)</div>
          <textarea
            className="input min-h-[100px] w-full"
            placeholder="I sang with the Glee Club from 2019 to 2023…"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </label>
        {submit.error && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-400">
            {submit.error.serverMessage ?? "Submission failed."}
          </div>
        )}
        <button type="submit" className="btn-primary" disabled={submit.isPending}>
          <Send size={14} /> {submit.isPending ? "Sending…" : "Submit request"}
        </button>
      </form>
    </div>
  )
}
